// SetPassword.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/webmanager`;

const passwordRules = (pw) => [
  { ok: pw.length >= 8,          label: 'At least 8 characters'  },
  { ok: /[A-Z]/.test(pw),        label: 'One uppercase letter'   },
  { ok: /[a-z]/.test(pw),        label: 'One lowercase letter'   },
  { ok: /[0-9]/.test(pw),        label: 'One number'             },
  { ok: /[^A-Za-z0-9]/.test(pw), label: 'One special character'  },
];

const EyeIcon = ({ open }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const OFCLogo = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <rect width="32" height="32" rx="8" fill="#0ea5e9" />
    <text x="16" y="22" textAnchor="middle" fill="white"
      style={{ fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '13px', letterSpacing: '0.5px' }}>
      OFC
    </text>
  </svg>
);

const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showCf, setShowCf] = useState(false);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [tokenChecked, setTokenChecked] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);

  const rules = passwordRules(password);
  const allPass = rules.every((r) => r.ok);
  const match = password === confirm && confirm.length > 0;
  const strength = rules.filter(r => r.ok).length;

  useEffect(() => {
    if (!token || !email) {
      setStatus('expired');
      setMessage('Invalid or missing link. Please request a new one.');
      setTokenChecked(true);
      return;
    }
    (async () => {
      try {
        await axios.post(`${API_BASE}/set-password/verify`, { token, email });
        setTokenValid(true);
      } catch (err) {
        setStatus('expired');
        setMessage(err.response?.data?.message || 'This link has expired or is invalid. Please contact your administrator.');
      } finally {
        setTokenChecked(true);
      }
    })();
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allPass || !match) return;
    setStatus('loading');
    try {
      const res = await axios.post(`${API_BASE}/set-password/reset`, {
        token, email, password, password_confirmation: confirm,
      });
      const { token: authToken, user } = res.data;
      if (authToken) {
        localStorage.setItem('auth_token', authToken);
        localStorage.setItem('user', JSON.stringify(user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      }
      setStatus('success');
      setMessage(res.data?.message || 'Password set! Redirecting…');
      setTimeout(() => navigate(user?.role === 'client_admin' ? `/${user.username}/dashboard` : '/login'), 2000);
    } catch (err) {
      setStatus('error');
      setTokenValid(false);
      setMessage(
        err.response?.data?.message ||
        err.response?.data?.errors?.password?.[0] ||
        'Failed to set password. Please try again.'
      );
    }
  };

  const strengthColor = ['#e2e8f0', '#ef4444', '#f97316', '#eab308', '#22c55e', '#10b981'][strength];
  const strengthLabel = ['', 'Very weak', 'Weak', 'Fair', 'Good', 'Strong'][strength];

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { margin: 0; }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes shimmer {
      0%   { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    .ofc-page {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f0f4f8;
      font-family: 'DM Sans', sans-serif;
      padding: 24px;
      position: relative;
      overflow: hidden;
    }
    .ofc-page::before {
      content: '';
      position: fixed;
      top: -40%;
      right: -20%;
      width: 600px;
      height: 600px;
      background: radial-gradient(circle, rgba(14,165,233,0.12) 0%, transparent 70%);
      pointer-events: none;
    }
    .ofc-page::after {
      content: '';
      position: fixed;
      bottom: -30%;
      left: -15%;
      width: 500px;
      height: 500px;
      background: radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%);
      pointer-events: none;
    }
    .ofc-card {
      width: 100%;
      max-width: 420px;
      background: #ffffff;
      border-radius: 20px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.04), 0 20px 48px rgba(0,0,0,0.08);
      overflow: hidden;
      animation: fadeUp 0.5s ease both;
      position: relative;
      z-index: 1;
    }
    .ofc-header {
      background: linear-gradient(135deg, #0c4a6e 0%, #0369a1 50%, #0ea5e9 100%);
      padding: 28px 32px 24px;
      position: relative;
      overflow: hidden;
    }
    .ofc-header::before {
      content: '';
      position: absolute;
      top: -30px; right: -30px;
      width: 120px; height: 120px;
      border-radius: 50%;
      background: rgba(255,255,255,0.06);
    }
    .ofc-header::after {
      content: '';
      position: absolute;
      bottom: -20px; left: 40px;
      width: 80px; height: 80px;
      border-radius: 50%;
      background: rgba(255,255,255,0.04);
    }
    .ofc-brand {
      display: flex;
      align-items: center;
      gap: 10px;
      position: relative;
      z-index: 1;
    }
    .ofc-brand-name {
      font-family: 'DM Serif Display', serif;
      font-size: 20px;
      color: #fff;
      letter-spacing: 0.3px;
      line-height: 1;
    }
    .ofc-brand-tagline {
      font-size: 11px;
      color: rgba(255,255,255,0.6);
      letter-spacing: 1.5px;
      text-transform: uppercase;
      margin-top: 2px;
    }
    .ofc-body { padding: 28px 32px 32px; }
    .ofc-title {
      font-size: 20px;
      font-weight: 600;
      color: #0f172a;
      margin-bottom: 4px;
      letter-spacing: -0.3px;
    }
    .ofc-subtitle {
      font-size: 13px;
      color: #64748b;
      margin-bottom: 22px;
      line-height: 1.5;
    }
    .ofc-email-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: #f0f9ff;
      border: 1px solid #bae6fd;
      border-radius: 100px;
      padding: 5px 12px;
      font-size: 12.5px;
      color: #0369a1;
      font-weight: 500;
      margin-bottom: 20px;
    }
    .ofc-field { margin-bottom: 16px; }
    .ofc-label {
      display: block;
      font-size: 12px;
      font-weight: 600;
      color: #475569;
      margin-bottom: 6px;
      letter-spacing: 0.4px;
      text-transform: uppercase;
    }
    .ofc-input-wrap { position: relative; }
    .ofc-input {
      width: 100%;
      padding: 11px 42px 11px 14px;
      border-radius: 10px;
      border: 1.5px solid #e2e8f0;
      font-size: 14px;
      color: #0f172a;
      background: #f8fafc;
      outline: none;
      font-family: 'DM Sans', sans-serif;
      transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
    }
    .ofc-input:focus {
      border-color: #0ea5e9;
      background: #fff;
      box-shadow: 0 0 0 3px rgba(14,165,233,0.12);
    }
    .ofc-input.error { border-color: #fca5a5; background: #fff; }
    .ofc-eye {
      position: absolute; right: 12px; top: 50%;
      transform: translateY(-50%);
      background: none; border: none; cursor: pointer;
      color: #94a3b8; display: flex; align-items: center;
      padding: 0; transition: color 0.15s;
    }
    .ofc-eye:hover { color: #475569; }
    .ofc-strength-bar {
      display: flex; gap: 4px; margin-top: 8px; margin-bottom: 4px;
    }
    .ofc-strength-seg {
      height: 3px; flex: 1; border-radius: 2px;
      background: #e2e8f0;
      transition: background 0.3s;
    }
    .ofc-strength-label {
      font-size: 11px; color: #94a3b8; margin-bottom: 10px;
    }
    .ofc-rules {
      background: #f8fafc;
      border: 1px solid #f1f5f9;
      border-radius: 10px;
      padding: 10px 12px;
      margin-bottom: 16px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 5px;
    }
    .ofc-rule {
      display: flex; align-items: center; gap: 6px;
      font-size: 12px; color: #94a3b8;
    }
    .ofc-rule.ok { color: #059669; }
    .ofc-match {
      font-size: 12px; margin-top: -8px; margin-bottom: 16px;
    }
    .ofc-match.ok { color: #059669; }
    .ofc-match.err { color: #ef4444; }
    .ofc-btn {
      width: 100%;
      padding: 13px;
      border-radius: 10px;
      border: none;
      font-size: 14px;
      font-weight: 600;
      font-family: 'DM Sans', sans-serif;
      cursor: pointer;
      letter-spacing: 0.2px;
      transition: opacity 0.15s, transform 0.1s;
    }
    .ofc-btn:not(:disabled):hover { opacity: 0.92; transform: translateY(-1px); }
    .ofc-btn:not(:disabled):active { transform: translateY(0); }
    .ofc-btn.active {
      background: linear-gradient(135deg, #0369a1, #0ea5e9);
      color: #fff;
      box-shadow: 0 4px 14px rgba(14,165,233,0.35);
    }
    .ofc-btn.disabled {
      background: #e2e8f0;
      color: #94a3b8;
      cursor: not-allowed;
    }
    .ofc-spinner {
      width: 16px; height: 16px;
      border: 2px solid rgba(255,255,255,0.35);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.7s linear infinite;
      display: inline-block;
      vertical-align: middle;
      margin-right: 8px;
    }
    .ofc-alert {
      padding: 12px 14px;
      border-radius: 10px;
      font-size: 13.5px;
      line-height: 1.5;
      margin-bottom: 18px;
    }
    .ofc-alert.success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; }
    .ofc-alert.error   { background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; }
    .ofc-alert.expired { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; }
    .ofc-footer {
      text-align: center;
      margin-top: 20px;
      font-size: 13px;
      color: #94a3b8;
    }
    .ofc-footer a {
      color: #0369a1;
      font-weight: 600;
      text-decoration: none;
    }
    .ofc-footer a:hover { text-decoration: underline; }
    .ofc-loading {
      text-align: center;
      padding: 44px 32px;
    }
    .ofc-loading-ring {
      width: 38px; height: 38px;
      border: 3px solid #e2e8f0;
      border-top-color: #0ea5e9;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
      margin: 0 auto 14px;
    }
    .ofc-loading-text { font-size: 13.5px; color: #64748b; }
    .ofc-divider {
      height: 1px;
      background: linear-gradient(to right, transparent, #e2e8f0, transparent);
      margin: 20px 0;
    }
  `;

  const brand = (
    <div className="ofc-brand">
      <OFCLogo />
      <div>
        <div className="ofc-brand-name">OneFinCore</div>
        <div className="ofc-brand-tagline">Document Management</div>
      </div>
    </div>
  );

  if (!tokenChecked) {
    return (
      <div className="ofc-page">
        <style>{styles}</style>
        <div className="ofc-card">
          <div className="ofc-header">{brand}</div>
          <div className="ofc-loading">
            <div className="ofc-loading-ring" />
            <p className="ofc-loading-text">Verifying your link…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ofc-page">
      <style>{styles}</style>
      <div className="ofc-card">
        <div className="ofc-header">{brand}</div>
        <div className="ofc-body">

          {/* Expired */}
          {status === 'expired' && (
            <>
              <p className="ofc-title">Link Expired</p>
              <div className="ofc-alert expired">{message}</div>
              <p style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                Contact your administrator to resend the setup email.
              </p>
            </>
          )}

          {/* Success */}
          {status === 'success' && (
            <>
              <p className="ofc-title">Password Set!</p>
              <div className="ofc-alert success">{message}</div>
              <p style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                Redirecting you to your dashboard…
              </p>
            </>
          )}

          {/* Form */}
          {tokenValid && status !== 'success' && (
            <>
              <p className="ofc-title">Set your password</p>
              <p className="ofc-subtitle">Choose a strong password to secure your account.</p>

              {email && (
                <div className="ofc-email-chip">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  {email}
                </div>
              )}

              {status === 'error' && (
                <div className="ofc-alert error">{message}</div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="ofc-field">
                  <label className="ofc-label">New Password</label>
                  <div className="ofc-input-wrap">
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      className={`ofc-input${!allPass && password.length > 0 ? ' error' : ''}`}
                      autoComplete="new-password"
                    />
                    <button type="button" className="ofc-eye" tabIndex={-1} onClick={() => setShowPw(p => !p)}>
                      <EyeIcon open={showPw} />
                    </button>
                  </div>

                  {/* Strength bar */}
                  {password.length > 0 && (
                    <>
                      <div className="ofc-strength-bar">
                        {[1,2,3,4,5].map(i => (
                          <div key={i} className="ofc-strength-seg"
                            style={{ background: i <= strength ? strengthColor : '#e2e8f0' }} />
                        ))}
                      </div>
                      <div className="ofc-strength-label" style={{ color: strengthColor }}>
                        {strengthLabel}
                      </div>
                    </>
                  )}
                </div>

                {/* Rules */}
                {password.length > 0 && (
                  <div className="ofc-rules">
                    {rules.map(r => (
                      <div key={r.label} className={`ofc-rule${r.ok ? ' ok' : ''}`}>
                        {r.ok ? <CheckIcon /> : <XIcon />}
                        {r.label}
                      </div>
                    ))}
                  </div>
                )}

                <div className="ofc-field">
                  <label className="ofc-label">Confirm Password</label>
                  <div className="ofc-input-wrap">
                    <input
                      type={showCf ? 'text' : 'password'}
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      placeholder="Re-enter new password"
                      className={`ofc-input${confirm.length > 0 && !match ? ' error' : ''}`}
                      autoComplete="new-password"
                    />
                    <button type="button" className="ofc-eye" tabIndex={-1} onClick={() => setShowCf(p => !p)}>
                      <EyeIcon open={showCf} />
                    </button>
                  </div>
                </div>

                {confirm.length > 0 && (
                  <p className={`ofc-match ${match ? 'ok' : 'err'}`}>
                    {match ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={!allPass || !match || status === 'loading'}
                  className={`ofc-btn ${!allPass || !match || status === 'loading' ? 'disabled' : 'active'}`}
                >
                  {status === 'loading'
                    ? <><span className="ofc-spinner" />Setting password…</>
                    : 'Set Password & Continue'
                  }
                </button>
              </form>
            </>
          )}

          <div className="ofc-divider" />
          <div className="ofc-footer">
            Already have credentials?{' '}
            <Link to="/login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;